import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import anime from 'animejs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DEFAULT_INFINITE_SCROLL_CONFIG } from '../../../utils/infiniteScrollUtils';
import cancelIcon from '../../../assets/images/close-icon.svg';
import dataModel from '../../../models/DataModel';
import LoadingDots from '../../../components/LoadingDots';
import SelectProjectList from '../AddProjectGroupPopup/SelectProjectList';
import useAvailableProjectsPicker from '../AddProjectGroupPopup/useAvailableProjectsPicker';
import './EditProjectGroupPopup.scss';
import Input from '../../../components/Input';
import projectsService from '../../../services/projectsService';
import projectGroupService from '../../../services/projectGroupService';
import ProjectSearchFilter from '../../../components/ProjectsNav/ProjectSearchFilter';

const SCROLL_TARGET_ID = 'edit-available-projects-scroll';

const EditProjectGroupPopup = ({
  handleClosePopup,
  currentCompany,
  projectGroup,
  projectsList,
  handleEditGroup,
}) => {
  const [groupedProject, setGroupedProject] = useState([]);
  const [projectGroupName, setProjectGroupName] = useState(projectGroup.name);
  const [isSaveActive, setIsSaveActive] = useState(false);
  const [originalProjectIds] = useState(projectGroup.projects.map(p => p.id));

  const deactivatePopupRef = useRef(null);
  const inputFieldRef = useRef(null);
  const toBeAdded = useRef([]);
  const toBeRemoved = useRef([]);
  const current = useRef([]);

  const {
    projects,
    searchValue,
    loadMoreProjects,
    handleSearchChange,
    hasMore,
  } = useAvailableProjectsPicker();

  useEffect(() => {
    dataModel.setDisableAppBackground(true);
    if (inputFieldRef.current) inputFieldRef.current.focus();

    if (groupedProject.length === 0 && projectsList && projectsList.length > 0) {
      projectsService.getProjectsByIds(projectsList).then(response => {
        const list = (response.data || []).map(p => p.attributes || p);
        setGroupedProject(list);
        current.current = list;
      });
    }

    return () => dataModel.setDisableAppBackground(false);
  }, []);

  useEffect(() => {
    const hasNameChanged = projectGroup.name !== projectGroupName;
    const currentIds = groupedProject.map(p => String(p.id));
    const originalIds = originalProjectIds.map(id => String(id));
    const hasProjectsChanged =
      currentIds.length !== originalIds.length ||
      !currentIds.every(id => originalIds.includes(id)) ||
      !originalIds.every(id => currentIds.includes(id));
    setIsSaveActive(
      Boolean(
        projectGroupName &&
          projectGroupName.length > 0 &&
          (hasNameChanged || hasProjectsChanged)
      )
    );
  }, [projectGroupName, groupedProject, originalProjectIds, projectGroup.name]);

  const closePopup = useCallback(() => {
    dataModel.setDisableAppBackground(false);
    if (handleClosePopup) handleClosePopup();
  }, [handleClosePopup]);

  const collapsePopup = () => {
    anime({
      targets: deactivatePopupRef.current,
      height: 0,
      easing: 'easeInOutCubic',
      duration: 200,
      complete: closePopup,
    });
  };

  const handleGroupNameChange = e => {
    setProjectGroupName(e.target.value);
  };

  const setProjectsState = useCallback(() => {
    setGroupedProject([...toBeAdded.current, ...current.current]);
  }, []);

  const handleAddProject = useCallback(
    project => {
      if (toBeRemoved.current.some(p => p.id === project.id)) {
        toBeRemoved.current = toBeRemoved.current.filter(p => p.id !== project.id);
        current.current.push(project);
      } else {
        toBeAdded.current.push(project);
      }
      setProjectsState();
    },
    [setProjectsState]
  );

  const handleRemoveProject = useCallback(
    project => {
      toBeAdded.current = toBeAdded.current.filter(p => p.id !== project.id);
      current.current = current.current.filter(p => p.id !== project.id);
      if (!toBeRemoved.current.some(p => p.id === project.id)) {
        toBeRemoved.current.push(project);
      }
      setProjectsState();
    },
    [setProjectsState]
  );

  const availableList = useMemo(() => {
    const excludedIds = new Set(groupedProject.map(gp => gp.id));
    const hasSearch = searchValue != null && String(searchValue).trim() !== '';
    const list = hasSearch
      ? projects.filter(p => !excludedIds.has(p.id))
      : [
        ...projects.filter(p => !excludedIds.has(p.id)),
        ...toBeRemoved.current,
      ];
    const unique = Array.from(new Map(list.map(p => [p.id, p])).values());
    return unique.sort((a, b) =>
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    );
  }, [projects, groupedProject, searchValue]);

  const selectedProjectList = useMemo(
    () =>
      [...groupedProject].sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      ),
    [groupedProject]
  );

  const handleSave = () => {
    if (!isSaveActive) return;
    const projectIds = groupedProject.map(p => p.id);
    projectGroupService
      .updateProjectGroup(
        projectGroup.id,
        currentCompany.id,
        projectGroupName,
        projectIds
      )
      .then(response => {
        if (!response.error && handleEditGroup) handleEditGroup();
        closePopup();
      });
  };

  const handleKeyDown = e => {
    if (e.keyCode === 13 || e.keyCode === 40) handleSave();
    else if (e.keyCode === 27) collapsePopup();
  };

  return (
    <div className="edit-project-group-background">
      <div className="edit-project-group" ref={deactivatePopupRef}>
        <header className="edit-project-group__header">
          <div className="caption">Edit Project Group</div>
          <div className="close-button">
            <img src={cancelIcon} alt="cancel" onClick={collapsePopup} />
          </div>
        </header>

        <div className="edit-project-group__container">
          <div className="project-group-input-name">
            <label>Project Group Name:</label>
            <Input
              onChange={handleGroupNameChange}
              value={projectGroupName}
              onKeyDown={handleKeyDown}
              ref={inputFieldRef}
            />
          </div>
          <div className="project-group-selection">
            <div className="left-side">
              <div className="selection-section project-section">
                <div className="pane-caption">Available Projects</div>
                <div className="project-groups-search">
                  <ProjectSearchFilter
                    handleSearchChange={handleSearchChange}
                    hideFilter={true}
                  />
                </div>
                <div
                  id={SCROLL_TARGET_ID}
                  className="available-projects-scroll edit-available-projects-scroll"
                >
                  <InfiniteScroll
                    scrollableTarget={SCROLL_TARGET_ID}
                    dataLength={availableList.length}
                    next={loadMoreProjects}
                    hasMore={hasMore}
                    loader={
                      <div className="loading-container">
                        <LoadingDots />
                      </div>
                    }
                    endMessage={null}
                    scrollThreshold={
                      DEFAULT_INFINITE_SCROLL_CONFIG.scrollThreshold
                    }
                  >
                    <SelectProjectList
                      projects={availableList}
                      selectProject={handleAddProject}
                      type="available"
                    />
                  </InfiniteScroll>
                </div>
                <div className="instructions">Click to Add</div>
              </div>
            </div>
            <div className="right-side">
              <div className="selection-section project-section">
                <div className="pane-caption">Selected Projects</div>
                <SelectProjectList
                  projects={selectedProjectList}
                  selectProject={handleRemoveProject}
                  height={280}
                />
                <div className="instructions">Click to Remove</div>
              </div>
            </div>
          </div>
        </div>

        <div className="edit-project-group__button-container">
          <button className="cancel-button" onClick={collapsePopup}>
            CANCEL
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!isSaveActive}
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.new_companies.currentCompany,
});

export default withRouter(connect(mapStateToProps)(EditProjectGroupPopup));



// WEBPACK FOOTER //
// ./src/containers/ProjectSummaryContainer/EditProjectGroupPopup/index.js